import { useEffect, useRef } from 'react';
import { useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import type { ImportHistoryItem } from '@klareospace/pharos-lib-types';
import { usePersistedPageSize } from '@klareospace/pharos-lib-web-utils';

import { useMasterContext } from '&/master';

import type { ListImportsParams } from '../api';
import { useImports } from './use-imports';

// ─── Constants ────────────────────────────────────────────────────────────────

const IMPORT_HISTORY_PAGE_SIZE_STORAGE_KEY = 'import-history';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseImportHistoryPageReturn {
  currentPage: number;
  error: Error | null;
  imports: ImportHistoryItem[];
  isLoading: boolean;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSize: number;
  pageSizeOptions: number[];
  totalItems: number;
  totalPages: number;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

function toParams(
  currentPage: number,
  pageSize: number,
  siteId: string | null,
  spaceId: string | null,
): ListImportsParams {
  return {
    page: currentPage,
    pageSize,
    siteId: siteId ?? undefined,
    spaceId: spaceId ?? undefined,
  };
}

function toScopeKey(siteId: string | null, spaceId: string | null): string {
  return `${siteId ?? ''}|${spaceId ?? ''}`;
}

/** Composes the import-history page's data wiring — follows the master context (space/site) so the list refilters when the user changes scope. */
export function useImportHistoryPage(): UseImportHistoryPageReturn {
  const { siteId, spaceId } = useMasterContext();
  const {
    options: pageSizeOptions,
    pageSize,
    setPageSize,
  } = usePersistedPageSize(IMPORT_HISTORY_PAGE_SIZE_STORAGE_KEY);
  const searchState = useSearchPageState({ pageSize });

  const scopeKey = toScopeKey(siteId, spaceId);
  const prevScopeKeyRef = useRef(scopeKey);

  // Reset to page 1 synchronously on scope (site/space) change — avoids a wasted fetch with the
  // stale page number. Mirrors the prevTrimmedSearchRef/prevPageSizeRef idiom in useSearchPageState.
  const currentPage = prevScopeKeyRef.current !== scopeKey ? 1 : searchState.currentPage;

  useEffect(() => {
    if (prevScopeKeyRef.current !== scopeKey) {
      prevScopeKeyRef.current = scopeKey;
      searchState.resetPage();
    }
  }, [scopeKey, searchState.resetPage]);

  const params = toParams(currentPage, searchState.pageSize, siteId, spaceId);
  const { data, error, isLoading } = useImports(params);

  return {
    currentPage,
    error: error ?? null,
    imports: data?.items ?? [],
    isLoading,
    onPageChange: searchState.onPageChange,
    onPageSizeChange: setPageSize,
    pageSize: searchState.pageSize,
    pageSizeOptions,
    totalItems: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
  };
}
